import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, KeyRoundIcon, Loader2Icon } from 'lucide-react';
import type { Project, ResolvedBrand } from '../types/project';
import { BrandLogo } from './BrandLogo';
interface ProjectPasswordPromptProps {
  project: Project;
  brand: ResolvedBrand;
  onUnlock: () => void;
  onCancel: () => void;
}
export function ProjectPasswordPrompt({
  project,
  brand,
  onUnlock,
  onCancel
}: ProjectPasswordPromptProps) {
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (status === 'checking' || value.trim().length === 0) return;
    setStatus('checking');
    window.setTimeout(() => {
      if (value.trim() === project.password) {
        onUnlock();
      } else {
        setStatus('error');
        setValue('');
        inputRef.current?.focus();
      }
    }, 480);
  }
  return <motion.main initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="mx-auto flex w-full max-w-md flex-col px-6 pb-24 pt-16" style={{
    fontFamily: 'var(--b-body)'
  }} data-id="element-77" data-mp="2sddmrfxj1bqh">
      <BrandLogo brand={brand} size="lg" data-id="element-78" data-mp="375xl839qev1l" />

      <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em]" style={{
      color: 'var(--b-accent)'
    }} data-id="element-79" data-mp="3c3potujqo5lm">
        Step 2 of 2
      </p>
      <h1 className="mt-3 text-3xl font-bold leading-tight" style={{
      fontFamily: 'var(--b-display)',
      color: 'var(--b-text)',
      textTransform: brand.displayUppercase ? 'uppercase' : 'none'
    }} data-id="element-80" data-mp="3g380s0tl16cb">
        {project.name}
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-[var(--b-dim)]" data-id="element-81" data-mp="1qrny1ch87ccc">
        This project has its own password. Ask the project lead if you don&apos;t have it yet.
      </p>

      <motion.form onSubmit={handleSubmit} animate={status === 'error' ? {
      x: [0, -8, 7, -5, 0]
    } : {
      x: 0
    }} transition={{
      duration: 0.36
    }} style={{
      borderRadius: 'var(--b-radius)'
    }} className="mt-8 border border-[var(--b-border)] bg-[var(--b-surface)] p-6" noValidate data-id="element-82" data-mp="1npjeh3g2n65f">
        <label htmlFor="project-password" className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--b-dim)]" data-id="element-83" data-mp="1sm9whwbfsbth">
          <KeyRoundIcon className="h-3 w-3" aria-hidden="true" data-id="element-84" data-mp="22l96pjbuofil" />
          Project password
        </label>
        <input id="project-password" ref={inputRef} type="password" autoComplete="off" autoFocus value={value} onChange={event => {
        setValue(event.target.value);
        if (status === 'error') setStatus('idle');
      }} aria-invalid={status === 'error'} aria-describedby={status === 'error' ? 'project-password-error' : undefined} placeholder="••••••••••" style={{
        borderRadius: 'var(--b-radius)'
      }} className="w-full border border-[var(--b-border)] bg-[var(--b-surface-alt)] px-4 py-3 text-sm text-[var(--b-text)] outline-none transition placeholder:text-[var(--b-dim)] focus:border-[var(--b-accent)] focus:ring-2 focus:ring-[var(--b-accent)]" data-id="element-85" data-mp="2gcnm7g6jtykw" />

        <div aria-live="polite" className="min-h-[20px]" data-id="element-86" data-mp="2l8hx4703102o">
          {status === 'error' && <p id="project-password-error" className="mt-2 text-xs font-medium text-red-500" data-id="element-87" data-mp="2q2cev4w4n4z4">
              Wrong password for {project.name}.
            </p>}
        </div>

        <div className="mt-4 flex gap-2" data-id="element-88" data-mp="2h9979d85xmce">
          <button type="button" onClick={onCancel} style={{
          borderRadius: 'var(--b-radius)'
        }} className="inline-flex items-center gap-2 border border-[var(--b-border)] bg-[var(--b-surface-alt)] px-4 py-3 text-sm font-medium text-[var(--b-dim)] transition hover:text-[var(--b-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-89" data-mp="2h63ftrc2a8di">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-90" data-mp="30v4j809kaqpz" />
            Back
          </button>
          <button type="submit" disabled={status === 'checking' || value.trim().length === 0} style={{
          borderRadius: 'var(--b-radius)',
          backgroundColor: 'var(--b-accent)',
          color: 'var(--b-on-accent)'
        }} className="inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-91" data-mp="2vwt8lnbg0c6p">
            {status === 'checking' ? <>
                <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-92" data-mp="b6vjlq603z6i" />
                Verifying
              </> : 'Open project'}
          </button>
        </div>
      </motion.form>
    </motion.main>;
}